import { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import type { AxiosInstance } from 'axios'
import { useAuthentication, AuthenticationStatus } from '@mysaiva/core/authentication'
import { LoadingPage } from '@mysaiva/core'
import { DEVELOPER_PLATFORM_PATH } from '@mysaiva/core/developer-platform'
import { redirectToProduct, getAvailableProducts, type UserMeRead } from '../utils/productRedirect'
import { shouldOpenDeveloperPlatform } from '../utils/developerRedirect'
import { NoProductsScreen } from './NoProductsScreen'

/**
 * Component that handles post-login redirection logic:
 * 1. Waits for authentication status to load
 * 2. If not authenticated: redirects to /login
 * 3. If authenticated with products:
 *    - Developer-role users with `developer_platform:manage` on their UPT org
 *      open the Developer Platform (`/integrations`) instead of a product
 *    - Checks localStorage for last selected product (key: 'mysaiva-last-app')
 *    - If last selected product is in user's product_list: redirects there
 *    - Otherwise: redirects to first product in user's product_list
 * 4. If authenticated with no products: shows NoProductsScreen (Access denied)
 *
 * The localStorage key 'mysaiva-last-app' is shared with the ProductSwitcher
 * component in @mysaiva/core to maintain consistency across the application.
 */
interface PostLoginRedirectProps {
  apiClient: AxiosInstance
}

export function PostLoginRedirect({ apiClient }: PostLoginRedirectProps) {
  const { user, status } = useAuthentication()
  const navigate = useNavigate()
  const [shouldRedirect, setShouldRedirect] = useState(false)

  const isLoading = status === AuthenticationStatus.Loading

  useEffect(() => {
    if (!isLoading && user) {
      // Check if user has access to any products
      const availableProducts = getAvailableProducts(user as UserMeRead)

      if (availableProducts.length > 0) {
        setShouldRedirect(true)
        let cancelled = false
        let timer: ReturnType<typeof setTimeout> | undefined

        shouldOpenDeveloperPlatform(apiClient, user as UserMeRead).then(openWorkspace => {
          if (cancelled) return
          if (openWorkspace) {
            // The workspace is served by this app, so no full page load.
            navigate(DEVELOPER_PLATFORM_PATH, { replace: true })
            return
          }
          // Small delay to show loading state before redirect
          timer = setTimeout(() => {
            redirectToProduct(user as UserMeRead)
          }, 500)
        })

        return () => {
          cancelled = true
          clearTimeout(timer)
        }
      }
    }
  }, [user, isLoading, apiClient, navigate])

  // Show loading screen while checking authentication
  if (isLoading) {
    return <LoadingPage />
  }

  // Not authenticated - redirect to login
  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Show loading screen while redirecting to product
  if (shouldRedirect) {
    return <LoadingPage />
  }

  // User is authenticated but has no products
  const availableProducts = getAvailableProducts(user as UserMeRead)
  if (availableProducts.length === 0) {
    return <NoProductsScreen />
  }

  // Fallback - should not reach here
  return <LoadingPage />
}
